/**
 * Consultant Feedback service.
 *
 * Single submission path: writes a `consultantFeedback/{id}` doc. A backend
 * onCreate trigger (`functions/src/sendFeedbackEmail.ts`) handles the email
 * notification asynchronously, so a SendGrid hiccup never blocks the
 * consultant's UI confirmation.
 */

import {
  collection,
  addDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase/config';
import type { FeedbackFormData, FeedbackStatus, FeedbackSubmission } from '../types/feedback.types';
import type { User } from '../types/user.types';

const COLLECTION = 'consultantFeedback';
const STORAGE_PREFIX = 'consultantFeedback/uploads';
const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024; // 5MB cap

export async function submitFeedback(
  form: FeedbackFormData,
  userProfile: User,
  submittedFromUrl: string,
  userAgent: string,
): Promise<{ id: string }> {
  // Optional screenshot: upload to Storage FIRST so the doc carries the URL.
  // If the upload fails, we still save the rest of the feedback — losing a
  // screenshot is far better than losing the submission.
  let screenshotUrl: string | undefined;
  let screenshotPath: string | undefined;
  if (form.screenshot) {
    if (form.screenshot.size > MAX_SCREENSHOT_BYTES) {
      throw new Error('Screenshot is too large. Maximum size is 5MB.');
    }
    try {
      const ext = (form.screenshot.name.split('.').pop() || 'png').toLowerCase().slice(0, 8);
      const filename = `${userProfile.id}_${Date.now()}.${ext}`;
      screenshotPath = `${STORAGE_PREFIX}/${filename}`;
      const ref = storageRef(storage, screenshotPath);
      await uploadBytes(ref, form.screenshot, {
        contentType: form.screenshot.type || 'image/png',
        customMetadata: {
          consultantId: userProfile.id,
          consultantEmail: userProfile.email || '',
        },
      });
      screenshotUrl = await getDownloadURL(ref);
    } catch (err) {
      console.error('[feedbackService] Screenshot upload failed, continuing without:', err);
      screenshotUrl = undefined;
      screenshotPath = undefined;
    }
  }

  // Dual-ID safety: persist both the doc ID and the Auth UID so reports later
  // can be correlated either way (mirrors the pattern in the consultant CRM
  // audit findings — some legacy contacts still use the Auth UID form).
  const submission: Omit<FeedbackSubmission, 'createdAt'> & { createdAt: Timestamp } = {
    consultantId: userProfile.id,
    consultantAuthUid: userProfile.uid || userProfile.id,
    consultantName: `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim() || 'Unknown',
    consultantEmail: userProfile.email || '',
    consultantRole: userProfile.role || 'unknown',

    whereInSystem: form.whereInSystem.trim(),
    category: form.category,
    severity: form.severity,
    description: form.description.trim(),

    submittedFromUrl,
    userAgent,

    status: 'new',
    emailSent: false,
    createdAt: Timestamp.now(),
    ...(screenshotUrl ? { screenshotUrl, screenshotPath } : {}),
  };

  const ref = await addDoc(collection(db, COLLECTION), submission);
  return { id: ref.id };
}

/**
 * Support Hub additions — live subscription + triage writes for the
 * consultantFeedback queue (its status/triagedAt/resolution lifecycle fields
 * existed from day one but no UI ever wrote them until the hub).
 */
export function subscribeToConsultantFeedback(
  cb: (items: Array<FeedbackSubmission & { id: string }>) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    query(collection(db, COLLECTION), orderBy('createdAt', 'desc')),
    (snap) => {
      cb(snap.docs.map((d) => ({ ...(d.data() as FeedbackSubmission), id: d.id })));
    },
    (err) => {
      console.error('[feedbackService] subscription failed:', err);
      onError?.(err);
    },
  );
}

export async function updateFeedbackTriage(
  id: string,
  patch: {
    status?: FeedbackStatus;
    assignedTo?: string | null;
    assignedToName?: string | null;
    resolution?: string | null;
    promotedTicketId?: string;
  },
): Promise<void> {
  await updateDoc(doc(db, COLLECTION, id), {
    ...patch,
    triagedAt: serverTimestamp(),
  });
}
